import { useNavigate, useSearchParams } from "react-router-dom";

import { Material, Transaction as TransactionType } from "/app/src/models";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import { createColumnHelper } from "@tanstack/react-table";
import { materialService, transactionService } from "/app/src/services";
import { Helmet } from "react-helmet";
import Table from "/app/src/components/generic/tables/table";
import { HistoryTypes } from "/app/src/constants/types";
import useQueryParams from "/app/src/hooks/useQueryParams";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Autocomplete,
  AutocompleteItem,
  Select,
  SelectItem,
} from "@heroui/react";
import useSetting from "/app/src/components/settings/setting";
import {
  buildExplorerColumns,
  isColumnSelected,
  toggleExplorerColumn,
} from "/app/src/components/explorer/helpers/columns";
import HeroSearch from "/app/src/components/generic/components/heroSearch";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import DateTime from "/app/src/components/generic/formatting/dateTime";
import { useQuery } from "@tanstack/react-query";
import { useAuthState } from "/app/src/contexts/authentication";

/**
 *
 * @returns List of Transactions
 */
export default function Transactions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );
  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [filter, setFilter] = useState("");
  const [materialName, setMaterialName] = useState("");

  const { user } = useAuthState();
  const columnHelper = createColumnHelper<TransactionType>();

  const { data: materialNames } = useQuery({
    queryKey: ["materials"],
    queryFn: () => {
      return materialService.getAll();
    },
    initialData: { materials: [] },
    select: (data: { materials: Material[] }) => {
      return data.materials.map((material) => material.name);
    },
  });

  const availableColumns = [
    { id: "creationDate", label: t("translation:creation_date") },
    { id: "type", label: t("translation:type") },
    { id: "exportStateType", label: t("translation:export_state") },
    { id: "motiveType", label: t("translation:motive_type") },
    { id: "number", label: t("translation:line_number") },
  ];

  const { settingValue, createUpdateSetting } = useSetting({
    name: "explorerColumns",
    userId: user?.id,
  });

  const isColumnChecked = useCallback(
    (id: string) => isColumnSelected(settingValue, "transaction", id),
    [settingValue],
  );

  const toggleColumnSelection = useCallback(
    async (id: string) =>
      toggleExplorerColumn(
        settingValue,
        createUpdateSetting,
        "transaction",
        id,
        user?.id,
      ),
    [settingValue, createUpdateSetting, user?.id],
  );

  const columns = useMemo(() => {
    /**
     * Creates a column definition based on the given id.
     * @param {string} id - The identifier of the column to create.
     * @returns {Object|null} The column definition object or null if the id is not recognized.
     */
    const makeColumn = (id: string) => {
      switch (id) {
        case "orderName":
          return columnHelper.accessor("orderName", {
            id: "orderName",
            header: () => t("translation:order_name"),
            cell: (info) => info.getValue(),
          });
        case "creationDate":
          return columnHelper.accessor("creationDate", {
            id: "creationDate",
            header: () => t("translation:creation_date"),
            cell: (info) => <DateTime date={info.getValue()} />,
          });
        case "type":
          return columnHelper.accessor("type", {
            id: "type",
            cell: (info) => info.getValue(),
            header: () => t("translation:type"),
          });
        case "exportStateType":
          return columnHelper.accessor("exportStateType", {
            id: "exportStateType",
            cell: (info) => info.getValue(),
            header: () => t("translation:export_state"),
          });
        case "motiveType":
          return columnHelper.accessor("motiveType", {
            id: "motiveType",
            cell: (info) => info.getValue(),
            header: () => t("translation:motive_type"),
          });
        case "id":
          return columnHelper.accessor("id", {
            id: "id",
            cell: (info) => info.getValue(),
            header: () => t("translation:id"),
          });
        case "number":
          return columnHelper.accessor("number", {
            id: "number",
            cell: (info) => info.getValue(),
            header: () => t("translation:line_number"),
          });
        default:
          return null;
      }
    };

    return buildExplorerColumns(settingValue, "transaction", makeColumn, [
      "id",
      "orderName",
    ]);
  }, [columnHelper, settingValue, t]);

  const createToggleHandler = useCallback(
    (id: string) => () => {
      toggleColumnSelection(id);
    },
    [toggleColumnSelection],
  );

  /**
   *
   * @param id Transaction ID
   * @returns Navigate to the Transaction
   */
  const goToTransaction = useCallback(
    (id: number) => {
      navigate(`/explorer/transactions/${id}`);
    },
    [navigate],
  );

  const {
    data: transactions,
    count: transactionsCount,
    isFetching,
  } = usePaginatedData<TransactionType>({
    queryKey: ["transactions"],
    searchString,
    sort,
    page,
    pageSize,
    service: transactionService,
    options: {
      ...(filter !== "" ? { type: `[or]${filter}` } : {}),
      //materialName filter is set to null if the component is cleared
      ...(materialName !== null && materialName !== "" ? { materialName } : {}),
    },
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: transactionsCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  //onChange for select setFilter Callback
  const onChangeFilter = useCallback(
    (value: string | string[] | React.ChangeEvent<HTMLSelectElement>) => {
      let deliminatedValue: string;
      if (typeof value === "string") {
        deliminatedValue = value;
      } else if (Array.isArray(value)) {
        deliminatedValue = value.join(";");
      } else {
        deliminatedValue = value.target.value;
      }
      setFilter(deliminatedValue);
    },
    [setFilter],
  );

  const onAutoCompleteSelection = useCallback(
    (value: React.Key) => {
      const stringValue =
        value === undefined || value === null ? "" : String(value);
      setMaterialName(stringValue === "" ? null : stringValue);
      setPage(0);
    },
    [setPage],
  );

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:transactions")} - ItemPath</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: t("translation:transactions") },
        ]}
      />

      <div className="mt-12 mb-4 flex justify-between">
        <div className="flex justify-between gap-2">
          <HeroSearch
            className="w-[300px]"
            setSearchString={setSearchString}
            allowClear
            box={false}
          />
          <Autocomplete
            aria-label="Filter by material"
            className="w-[300px]"
            value={materialName ?? ""}
            onSelectionChange={onAutoCompleteSelection}
            placeholder="Filter by Material"
          >
            {(materialNames || []).map((name) => (
              <AutocompleteItem key={name}>{name}</AutocompleteItem>
            ))}
          </Autocomplete>
          <Select
            aria-label="Filter by type"
            size="md"
            onChange={onChangeFilter}
            selectionMode="multiple"
            className="w-[300px]"
            placeholder={t("translation:filter_by_priority")}
            items={[
              { name: t("translation:all"), value: "all" },
              ...HistoryTypes,
            ]}
          >
            {(item) => <SelectItem key={item.value}>{item.name}</SelectItem>}
          </Select>
        </div>
        <Popover placement="bottom" radius="md" aria-label="Customize columns">
          <PopoverTrigger>
            <Button size="md" radius="md" color="primary">
              {t("translation:customize_columns")}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="w-[300px] p-4">
              <h3 className="text-[16px]">
                {t("translation:available_columns")}
              </h3>
              <div className="mt-2 flex flex-col gap-2">
                {availableColumns.map((column) => (
                  <Checkbox
                    size="sm"
                    key={column.id}
                    defaultSelected={isColumnChecked(column.id)}
                    onChange={createToggleHandler(column.id)}
                  >
                    {column.label}
                  </Checkbox>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <Table
        loading={isFetching}
        rows={transactions}
        tableColumns={columns}
        length={transactionsCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        rowClicked={goToTransaction}
        emptyText={`${t("translation:no")} ${t("translation:transactions")} ${t(
          "translation:found",
        )}`}
      />
    </div>
  );
}
